import React, {useCallback, useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {useSteps} from '@chakra-ui/react'
import logger from '../../../utils/logger-instance'

const WarrantyContext = React.createContext()

export const WARRANTY_MODES = {
    CPSIA: 'cpsia',
    WARRANTY: 'warranty'
}

const STEPS_LIST = {
    [WARRANTY_MODES.CPSIA]: [
        'PRODUCT_SELECTION',
        'PRODUCT_DETAILS',
        'POINT_OF_PURCHASE',
        'CUSTOMER_DATA'
    ],
    [WARRANTY_MODES.WARRANTY]: ['PRODUCT_SELECTION', 'PRODUCT_DETAILS', 'POINT_OF_PURCHASE']
}
export const WARRANTY_STEPS = Object.fromEntries(
    Object.entries(STEPS_LIST).map(([mode, list]) => [
        mode,
        Object.fromEntries(list.map((step, idx) => [step, idx]))
    ])
)

export const WarrantyProvider = ({
    children,
    config,
    mode,
    initialData = {},
    isSubmitting = false,
    onSubmit: onSubmitCompleted
}) => {
    const count = STEPS_LIST[mode].length
    const {
        activeStep: step,
        goToNext: goToNextStep,
        goToPrevious: goToPreviousStep
    } = useSteps({
        index: 0,
        count: count
    })
    const isFirstStep = step === 0
    const isLastStep = step === count - 1
    const [data, setData] = useState(initialData)

    const onSubmit = useCallback(
        async (stepData) => {
            logger.log(`Step ${step + 1}/${count} data: `, stepData)
            const newData = {...data, ...stepData}
            setData(newData)

            if (isLastStep) {
                try {
                    await onSubmitCompleted(newData)
                } catch {
                    // TODO: maybe we should move to specific step and highlight specific field based on the error???
                }
            } else {
                goToNextStep()
            }
        },
        [count, data, isLastStep, step, goToNextStep, onSubmitCompleted]
    )

    const value = useMemo(
        () => ({
            config,
            data,
            isFirstStep,
            isLastStep,
            isSubmitting,
            mode,
            step,
            steps: STEPS_LIST[mode],
            STEPS: WARRANTY_STEPS[mode],
            goToPreviousStep,
            goToNextStep,
            onSubmit
        }),
        [
            config,
            data,
            isFirstStep,
            isLastStep,
            isSubmitting,
            mode,
            step,
            goToPreviousStep,
            goToNextStep,
            onSubmit
        ]
    )

    return <WarrantyContext.Provider value={value}>{children}</WarrantyContext.Provider>
}

WarrantyProvider.propTypes = {
    children: PropTypes.node.isRequired,
    config: PropTypes.object,
    initialData: PropTypes.object,
    isSubmitting: PropTypes.bool,
    mode: PropTypes.oneOf(Object.values(WARRANTY_MODES)).isRequired,
    onSubmit: PropTypes.func.isRequired
}

/**
 * Custom hook to use the warranty context.
 * @returns {{
 *   config: object,
 *   data: object,
 *   isLastStep: boolean,
 *   isSubmitting: boolean,
 *   mode: 'cpsia'|'warranty',
 *   step: number,
 *   steps: Array<string>,
 *   STEPS: Record<string, number>,
 *   goToPreviousStep: function(): void,
 *   goToNextStep: function(): void,
 *   onSubmit: function(object): Promise<void>
 * }}
 */
export const useWarrantyContext = () => {
    const context = React.useContext(WarrantyContext)
    if (context === undefined) {
        throw new Error('useWarrantyContext must be used within a WarrantyProvider')
    }
    return context
}
